import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { removeEvent, updateEvent } from 'flow/actions/calendar-actions';
import CalendarOccurrenceDropdown from 'flow/components/calendar/CalendarOccurrenceDropdown';
import CalendarProfilePicker from 'flow/components/calendar/CalendarProfilePicker';
import SettingSection from 'flow/components/Setting/SettingSection';
import BaseInput from 'flow/components/ui-components/BaseInput';
import BaseModal from 'flow/components/ui-components/BaseModal';
import TimeInput from 'flow/components/ui-components/TimeInput';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch } from 'flow/hooks/useRedux';
import EmbeddedType from 'flow/types/embedded-type';
import { isExtensionKey, ISO_8601_MS_RESOLUTION } from 'flow/utils';
import { isAllDayEvent } from 'flow/utils/calendar-utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var EditCalendarEventModal = function EditCalendarEventModal(_ref) {
  var entityKey = _ref.entityKey,
    calendarEvent = _ref.calendarEvent;
  var _useAppContext = useAppContext(),
    embedded = _useAppContext.embedded,
    i18next = _useAppContext.i18next;
  var dispatch = useAppDispatch();
  var profileTitle = isExtensionKey(entityKey) ? i18next.t('calendar.profile') : i18next.t('pbx.pbxmode');
  var _useState = useState({
      id: calendarEvent.getIn(['profile', 'key']),
      name: calendarEvent.getIn(['profile', 'description'])
    }),
    _useState2 = _slicedToArray(_useState, 2),
    selectProfile = _useState2[0],
    setSelectProfile = _useState2[1];
  var _useState3 = useState(isAllDayEvent(calendarEvent)),
    _useState4 = _slicedToArray(_useState3, 2),
    allDay = _useState4[0],
    setAllDay = _useState4[1];
  var timeRefStart = useRef(null);
  var dateRefStart = useRef(null);
  var timeRefEnd = useRef(null);
  var dateRefEnd = useRef(null);
  var _useState5 = useState({
      start: moment(calendarEvent.get('start')).format(ISO_8601_MS_RESOLUTION),
      end: moment(calendarEvent.get('end')).format(ISO_8601_MS_RESOLUTION)
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    formattedTime = _useState6[0],
    setFormattedTime = _useState6[1];
  var getOption = function getOption() {
    if (!calendarEvent.get('occurence') && !calendarEvent.get('interval')) {
      return i18next.t('calendar.occurence.never');
    }
    if (calendarEvent.get('occurence') === 'WEEKLY' && calendarEvent.get('interval') === 1) {
      return i18next.t('every_week');
    } else if (calendarEvent.get('occurence') === 'WEEKLY' && calendarEvent.get('interval') === 2) {
      return i18next.t('every_other_week');
    } else if (calendarEvent.get('occurence') === 'WEEKLY' && calendarEvent.get('interval') === 3) {
      return i18next.t('every_third_week');
    } else if (calendarEvent.get('occurence') === 'WEEKLY' && calendarEvent.get('interval') === 4) {
      return i18next.t('every_fourth_week');
    }
  };
  var _useState7 = useState({
      occurrence: calendarEvent.get('occurence') || null,
      interval: calendarEvent.get('interval') || null,
      name: getOption()
    }),
    _useState8 = _slicedToArray(_useState7, 2),
    repeat = _useState8[0],
    setRepeat = _useState8[1];
  var isInitialMount = useRef(true);
  useEffect(function () {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setDateTime();
  }, [allDay]);
  var setDateTime = function setDateTime() {
    if (allDay) {
      var _dateRefStart$current;
      var startMoment = moment("".concat((_dateRefStart$current = dateRefStart.current) === null || _dateRefStart$current === void 0 ? void 0 : _dateRefStart$current.value, " 00:00"), 'YYYY-MM-DD HH:mm');
      if (!startMoment.isValid()) return;
      setFormattedTime({
        start: startMoment.format(ISO_8601_MS_RESOLUTION),
        end: startMoment.clone().add(1, 'days').format(ISO_8601_MS_RESOLUTION)
      });
    } else {
      var _dateRefStart$current2, _timeRefStart$current, _dateRefEnd$current, _timeRefEnd$current;
      var _startMoment = moment("".concat((_dateRefStart$current2 = dateRefStart.current) === null || _dateRefStart$current2 === void 0 ? void 0 : _dateRefStart$current2.value, " ").concat((_timeRefStart$current = timeRefStart.current) === null || _timeRefStart$current === void 0 ? void 0 : _timeRefStart$current.value), 'YYYY-MM-DD HH:mm');
      var endMoment = moment("".concat((_dateRefEnd$current = dateRefEnd.current) === null || _dateRefEnd$current === void 0 ? void 0 : _dateRefEnd$current.value, " ").concat((_timeRefEnd$current = timeRefEnd.current) === null || _timeRefEnd$current === void 0 ? void 0 : _timeRefEnd$current.value), 'YYYY-MM-DD HH:mm');
      if (!_startMoment.isValid() || !endMoment.isValid()) return;
      setFormattedTime({
        start: _startMoment.format(ISO_8601_MS_RESOLUTION),
        end: endMoment.format(ISO_8601_MS_RESOLUTION)
      });
    }
  };
  var handleUpdateEvent = function handleUpdateEvent() {
    var updatedCalendarEvent = calendarEvent.set('profile', {
      key: selectProfile.id
    }).set('start', formattedTime.start).set('end', formattedTime.end).set('occurence', repeat.occurrence).set('interval', repeat.interval)["delete"]('rrule');
    dispatch(updateEvent(updatedCalendarEvent, entityKey));
  };
  return /*#__PURE__*/_jsx(BaseModal, {
    open: true,
    header: i18next.t('calendar.edit-event'),
    onRemove: function onRemove() {
      return dispatch(removeEvent(calendarEvent, entityKey));
    },
    onSubmit: handleUpdateEvent,
    disableSubmit: (formattedTime === null || formattedTime === void 0 ? void 0 : formattedTime.start) >= (formattedTime === null || formattedTime === void 0 ? void 0 : formattedTime.end) || moment().format(ISO_8601_MS_RESOLUTION) >= (formattedTime === null || formattedTime === void 0 ? void 0 : formattedTime.end),
    children: /*#__PURE__*/_jsxs("div", {
      className: "flex flex-col gap-3 pb-1",
      children: [/*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(SettingSection, {
          className: "px-0 text-sm/4",
          children: profileTitle
        }), /*#__PURE__*/_jsx(CalendarProfilePicker, {
          entityKey: entityKey,
          profile: selectProfile,
          onChange: setSelectProfile
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: "flex w-full items-center justify-between rounded-sm text-base hover:bg-gray-100",
        children: [/*#__PURE__*/_jsx("p", {
          children: i18next.t('calendar.all.day')
        }), /*#__PURE__*/_jsx(Toggle, {
          value: allDay,
          onChange: function onChange() {
            setAllDay(!allDay);
            calendarEvent.set('allDay', allDay);
          },
          text: i18next.t('calendar.all.day')
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: "flex flex-col gap-3",
        children: [/*#__PURE__*/_jsxs("div", {
          className: "flex flex-col gap-3",
          children: [/*#__PURE__*/_jsx(SettingSection, {
            className: "px-0 text-sm/4",
            children: i18next.t('calendar.start')
          }), /*#__PURE__*/_jsx(BaseInput, _objectSpread({
            type: "date",
            ref: dateRefStart,
            defaultValue: moment(calendarEvent.get('start')).format('YYYY-MM-DD'),
            min: moment().format('YYYY-MM-DD'),
            onChange: setDateTime
          }, embedded === EmbeddedType.IOS && {
            role: 'textbox',
            'aria-label': moment(formattedTime.start).format('MMMM D, YYYY')
          })), !allDay && /*#__PURE__*/_jsx(TimeInput, {
            ref: timeRefStart,
            defaultValue: moment(calendarEvent.get('start')).format('HH:mm'),
            onChange: setDateTime,
            ariaLabel: moment(formattedTime.start).format('HH:mm')
          })]
        }), !allDay && /*#__PURE__*/_jsxs("div", {
          className: "flex flex-col gap-3",
          children: [/*#__PURE__*/_jsx(SettingSection, {
            className: "px-0 text-sm/4",
            children: i18next.t('calendar.end')
          }), /*#__PURE__*/_jsx(BaseInput, _objectSpread({
            type: "date",
            ref: dateRefEnd,
            defaultValue: moment(calendarEvent.get('end')).format('YYYY-MM-DD'),
            min: moment().format('YYYY-MM-DD'),
            onChange: setDateTime
          }, embedded === EmbeddedType.IOS && {
            role: 'textbox',
            'aria-label': moment(formattedTime.end).format('MMMM D, YYYY')
          })), /*#__PURE__*/_jsx(TimeInput, {
            ref: timeRefEnd,
            defaultValue: moment(calendarEvent.get('end')).format('HH:mm'),
            onChange: setDateTime,
            ariaLabel: moment(formattedTime.end).format('HH:mm')
          })]
        })]
      }), /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(SettingSection, {
          className: "px-0 text-sm/4",
          children: i18next.t('calendar.repeat')
        }), /*#__PURE__*/_jsx(CalendarOccurrenceDropdown, {
          repeat: repeat,
          onChange: setRepeat
        })]
      })]
    })
  });
};
export default EditCalendarEventModal;